import React, { useState, useEffect } from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import CustomInput from "@components/Forms/CustomInput";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import { formatDate } from "@utils/common";
import { extractErrorCode } from "@utils/error";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { useMaskedPasswordCaret } from "@hooks/useMaskedPasswordCaret";
import ShowPasswordIcon from "@components/Icons/ShowPasswordIcon";
import HidePasswordIcon from "@components/Icons/HidePasswordIcon";

const ChangePasswordModal = ({
  isOpen,
  onClose,
  user,
  onPasswordChanged,
  requireCurrentPassword = true,
  "data-testid": dataTestId = "change-password-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [formData, setFormData] = useState({
    newPassword: "",
    currentPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { inputRef: currentPasswordRef, handleKeyDown: handleCurrentPasswordKeyDown } =
    useMaskedPasswordCaret();
  const { inputRef: newPasswordRef, handleKeyDown: handleNewPasswordKeyDown } =
    useMaskedPasswordCaret();
  const { inputRef: confirmPasswordRef, handleKeyDown: handleConfirmPasswordKeyDown } =
    useMaskedPasswordCaret();

  useEffect(() => {
    if (isOpen) {
      setFormData({
        newPassword: "",
        currentPassword: "",
        confirmPassword: "",
      });
      setValidationAttempted(false);
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (requireCurrentPassword && !formData.currentPassword?.trim()) {
      return false;
    }

    if (!formData.newPassword?.trim()) {
      return false;
    }

    if (formData.newPassword.trim().length < 6) {
      return false;
    }

    if (requireCurrentPassword && formData.currentPassword.trim() === formData.newPassword.trim()) {
      return false;
    }

    if (!formData.confirmPassword?.trim()) {
      return false;
    }

    if (formData.newPassword.trim() !== formData.confirmPassword.trim()) {
      return false;
    }

    return true;
  };

  const passwordEndpoint = requireCurrentPassword
    ? queryKeys.changeOwnPassword
    : queryKeys.resetUserPassword(user?.userId);

  const changePassword = useMutationData(
    passwordEndpoint,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error during change password:", error);
        const errorCode = extractErrorCode(error, "system.user.user-update-fail");
        useSuccessToast(TOAST.error, t("common.error"), t(errorCode, "Failed to change password"));
        setLoading(false);
      },
    },
  );

  const handleSubmit = async () => {
    setValidationAttempted(true);
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const payload = requireCurrentPassword
        ? {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword,
          }
        : {
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword,
          };
      await changePassword.mutateAsync(payload);
      ToastConstant(TOAST.success, t("system.user.password-changed-success"));
      onClose();
      onPasswordChanged();
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-50 flex items-center justify-start lg:justify-center ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto p-4`}
    >
      <div
        data-testid={dataTestId}
        className={`p-2 relative w-full max-w-lg ${colors.bgModal} transform overflow-hidden rounded-2xl shadow-xl transition-all duration-200 w-[512px] min-w-[512px]`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 pt-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-2xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("system.user.change-password", "CHANGE PASSWORD")}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Last Password Change Info */}
          <div className="mb-6" data-testid={fieldValueTestId("last-password-change-date")}>
            <p className={`${colors.textSecondary} text-sm`}>
              {t("system.user.last-password-change-date", "Last password change date")}: {formatDate(user.passwordChangedAt)}
            </p>
          </div>

          {/* Current Password Input */}
          {requireCurrentPassword && (<div className="mb-4" data-testid={fieldContainerTestId("current-password")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("current-password")}>
              {t("system.user.current-password", "Current Password")} <span className={colors.textDanger}>*</span>
            </label>
            <div className="relative">
              <CustomInput
                ref={currentPasswordRef}
                className="w-full pr-10"
                type="text"
                autoComplete="off"
                data-testid={fieldInputTestId("current-password")}
                placeholder={t("system.user.current-password-placeholder", "Enter current password")}
                value={
                  showCurrentPassword
                    ? formData.currentPassword
                    : "•".repeat(formData.currentPassword.length)
                }
                onChange={(e) => {
                  if (showCurrentPassword) {
                    handleInputChange("currentPassword", e.target.value);
                  }
                }}
                onKeyDown={(e) =>
                  handleCurrentPasswordKeyDown(
                    e,
                    formData.currentPassword,
                    (v) => handleInputChange("currentPassword", v),
                    showCurrentPassword
                  )
                }
              />
              <button
                type="button"
                data-testid={fieldInputTestId("toggle-current-password")}
                onClick={() => setShowCurrentPassword((v) => !v)}
                className="absolute top-0 right-0 h-10 pr-3 flex items-center justify-center"
                aria-label={showCurrentPassword ? "Hide password" : "Show password"}
              >
                {showCurrentPassword ? (
                  <HidePasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                ) : (
                  <ShowPasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                )}
              </button>
            </div>
            {validationAttempted && formData.currentPassword && formData.confirmPassword === formData.currentPassword && (
              <div className={`mt-1 text-xs ${colors.textDanger}`} data-testid={fieldErrorTestId("current-password")}>
                {t("system.user.new-password-same-as-old", "New password cannot be the same as the current password")}
              </div>
            )}
          </div>
          )}

          {/* New Password Input */}
          <div className="mb-4" data-testid={fieldContainerTestId("new-password")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("new-password")}>
              {t("system.user.new-password", "New Password")} <span className={colors.textDanger}>*</span>
            </label>
            <div className="relative">
              <CustomInput
                ref={newPasswordRef}
                className="w-full pr-10"
                type="text"
                autoComplete="off"
                data-testid={fieldInputTestId("new-password")}
                placeholder={t("system.user.new-password-placeholder", "Enter new password")}
                value={
                  showNewPassword
                    ? formData.newPassword
                    : "•".repeat(formData.newPassword.length)
                }
                onChange={(e) => {
                  if (showNewPassword) {
                    handleInputChange("newPassword", e.target.value);
                  }
                }}
                onKeyDown={(e) =>
                  handleNewPasswordKeyDown(
                    e,
                    formData.newPassword,
                    (v) => handleInputChange("newPassword", v),
                    showNewPassword
                  )
                }
              />
              <button
                type="button"
                data-testid={fieldInputTestId("toggle-new-password")}
                onClick={() => setShowNewPassword((v) => !v)}
                className="absolute top-0 right-0 h-10 pr-3 flex items-center justify-center"
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <HidePasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                ) : (
                  <ShowPasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                )}
              </button>
            </div>
            {validationAttempted && formData.newPassword.length > 0 && formData.newPassword.length < 6 && (
              <div className={`mt-1 text-xs ${colors.textDanger}`} data-testid={fieldErrorTestId("new-password")}>
                {t("system.user.password-min-length", "Password must be at least 6 characters")}
              </div>
            )}
          </div>

          {/* Confirm Password Input */}
          <div className="mb-6" data-testid={fieldContainerTestId("confirm-password")}>
            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("confirm-password")}>
              {t("system.user.confirm-password", "Confirm Password")} <span className={colors.textDanger}>*</span>
            </label>
            <div className="relative">
              <CustomInput
                ref={confirmPasswordRef}
                className="w-full pr-10"
                type="text"
                autoComplete="off"
                data-testid={fieldInputTestId("confirm-password")}
                placeholder={t("system.user.confirm-password-placeholder", "Re-enter new password")}
                value={
                  showConfirmPassword
                    ? formData.confirmPassword
                    : "•".repeat(formData.confirmPassword.length)
                }
                onChange={(e) => {
                  if (showConfirmPassword) {
                    handleInputChange("confirmPassword", e.target.value);
                  }
                }}
                onKeyDown={(e) =>
                  handleConfirmPasswordKeyDown(
                    e,
                    formData.confirmPassword,
                    (v) => handleInputChange("confirmPassword", v),
                    showConfirmPassword
                  )
                }
              />
              <button
                type="button"
                data-testid={fieldInputTestId("toggle-confirm-password")}
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute top-0 right-0 h-10 pr-3 flex items-center justify-center"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <HidePasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                ) : (
                  <ShowPasswordIcon
                    className={`h-5 w-5 ${colors.textSecondary} ${colors.textHoverActive} transition-colors duration-200`}
                  />
                )}
              </button>
            </div>
            {validationAttempted && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <div className={`mt-1 text-xs ${colors.textDanger}`} data-testid={fieldErrorTestId("confirm-password")}>
                {t("system.user.passwords-not-match", "Passwords do not match")}
              </div>
            )}
          </div>


          {/* Action Buttons */}
          <div className="flex justify-end space-x-3" data-testid={`${dataTestId}-footer`}>
            <OutlineButton
              data-testid={`${dataTestId}-cancel-button`}
              onClick={onClose}
              disabled={loading}
            >
              {t("common.cancel-button", "Cancel")}
            </OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={handleSubmit}
              disabled={
                loading ||
                (requireCurrentPassword && !formData.currentPassword.trim()) ||
                !formData.newPassword.trim() ||
                !formData.confirmPassword.trim()
              }
            >
              {loading ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.toggleThumbBackground}`}></div>
                  {t("system.user.changing", "Changing")}
                </div>
              ) : (
                t("system.user.change-button", "Change")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal; 